import styled from 'styled-components'

import emailIcon from 'app/assets/icons/mail_rounded.svg'
import { Box, Flex } from 'app/components/design-system-components'
import SvgFacebookRounded from 'app/components/design-system-components/icons/social/FacebookRounded'
import SvgGoogleRounded from 'app/components/design-system-components/icons/social/GoogleRounded'
import * as Display from 'app/components/display/display'
import {
  Admin,
  BDM,
  CasualInspector,
  Finance,
  LeasingConsultant,
  PropertyManager,
  TeamLeader,
} from 'app/constants/roles'
import {
  formatAndPrefix,
  formatPhoneNumber,
} from 'app/utils/phonenumber/helpers'

const StyledTeamMemberRow = styled.div<{
  teamMemberRowClickEnabled?: boolean
}>`
  display: flex;
  padding: ${(props) => props.theme.space[5]}px;
  strong {
    display: block;
    padding-bottom: 5px;
  }
  .name-pill-info {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    width: 100%;
    font-size: ${(props) => props.theme.fontSizes[5]}px;
  }
  .phone-email-info {
    margin-top: ${(props) => props.theme.space[1]}px;
    display: flex;
    font-size: ${(props) => props.theme.fontSizes[4]}px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%;
    -webkit-box-align: center;
    align-items: center;
  }
  .phone-number {
    min-width: ${(props) => props.theme.width[18]}px;
    margin-right: ${(props) => props.theme.space[3]}px;
  }
  .email {
    max-width: ${(props) => props.theme.width[24]}px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  ${(props) => props.teamMemberRowClickEnabled && `cursor: pointer;`}

  @media (max-width: 768px) {
    padding: ${(props) => props.theme.space[4]}px;
    flex-direction: column;

    .phone-email-info {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: ${(props) => props.theme.space[2]}px;
      margin-top: ${(props) => props.theme.space[2]}px;
    }

    .phone-number {
      min-width: unset;
      margin-right: 0;
      margin-bottom: ${(props) => props.theme.space[1]}px;
    }

    .email {
      max-width: 100%;
      margin-bottom: ${(props) => props.theme.space[1]}px;
    }

    .name-pill-info {
      font-size: ${(props) => props.theme.fontSizes[4]}px;
      flex-direction: column;
      align-items: flex-start;
      gap: ${(props) => props.theme.space[1]}px;
    }
  }
`

const StyledDivider = styled(Flex)`
  height: ${(props) => props.theme.height[2]}px;
  width: ${(props) => props.theme.width[2]}px;
  background-color: ${(props) => props.theme.colors.gray400};
  border-radius: 50%;
  margin-right: ${(props) => props.theme.space[3]}px;
  @media (max-width: 768px) {
    display: none;
  }
`

const StyledTeamMemberWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${(props) => props.theme.space[3]}px;
  }
`

const NameAndContact = styled(Box)`
  font-size: ${(props) => props.theme.fontSizes[4]}px;
  width: ${(props) => props.theme.width[24]}px;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const Roles = styled(Box)`
  font-size: ${(props) => props.theme.fontSizes[3]}px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  width: ${(props) => props.theme.width[13]}px;
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: ${(props) => props.theme.space[2]}px;
  }
`

const LoginAndInvites = styled(Box)`
  width: ${(props) => props.theme.width[22]}px;
  font-size: ${(props) => props.theme.fontSizes[3]}px;
  line-height: 140%;
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: ${(props) => props.theme.space[2]}px;
  }
`

interface TeamMemberRowProps {
  item: any
  teamMemberRowClickEnabled?: boolean
  showToolsButton?: boolean
  memberTools?: Array<{
    enableTool?: boolean
    onClick?: (item: any) => void
    text?: string
    [key: string]: any
  }>
}

export const TeamMemberRow = ({
  item,
  teamMemberRowClickEnabled = false,
  showToolsButton = false,
  memberTools = [],
}: TeamMemberRowProps) => {
  const shownMemberTools = memberTools.map((tool) => {
    return { ...tool, onClick: () => tool.onClick && tool.onClick(item) }
  })
  const {
    firstName,
    lastName,
    mobilePhone = '',
    phone = '',
    homePhone = '',
    workPhone = '',
    mobileNumber = '',
    statusAccepted = false,
    phoneNumber = '',
    officeNumber = '',
  } = item
  const contactNumber =
    mobilePhone ||
    phone ||
    homePhone ||
    workPhone ||
    mobileNumber ||
    phoneNumber ||
    officeNumber

  const hasName = firstName || lastName
  const textForTeamContactNumber = formatAndPrefix(contactNumber, '', true)
  const teamContactNumber = formatPhoneNumber(contactNumber)

  const Icon = () => {
    if (item.alias?.facebook) {
      return <SvgFacebookRounded className="ml5 mr5" height="16" width="16" />
    }
    if (item.alias?.google) {
      return <SvgGoogleRounded className="ml5 mr5" height="16" width="16" />
    }
    return (
      <img
        className="ml5 mr5"
        src={emailIcon}
        height="16"
        width="16"
        alt="email"
      />
    )
  }

  const getName = () => {
    if (!hasName) {
      return ''
    }
    return `${firstName} ${lastName}`
  }

  const nameAndEmail = () => {
    return (
      <div className="phone-email-info">
        {textForTeamContactNumber && (
          <a href={`tel:${teamContactNumber}`} className="phone-number">
            {textForTeamContactNumber}
          </a>
        )}
        {item.email && textForTeamContactNumber && <StyledDivider />}
        <a className="email" href={`mailto:${item.email}`}>
          {item.email}
        </a>
      </div>
    )
  }

  const displayRoles = () => {
    const roles = []
    if (item.isAdmin) {
      roles.push(Admin)
    }
    if (item.isFinance) {
      roles.push(Finance)
    }
    if (item.isCasualInspector) {
      roles.push(CasualInspector)
    }
    if (item.isLeasingConsultant) {
      roles.push(LeasingConsultant)
    }
    if (item.isPropertyManager) {
      roles.push(PropertyManager)
    }
    if (item.isBDM) {
      roles.push(BDM)
    }
    if (item.isTeamLeader) {
      roles.push(TeamLeader)
    }
    return (
      <>
        {roles.map((text, index) => (
          <div key={index}>
            {text}
            {index < roles.length - 1 && (
              <>
                ,<br />
              </>
            )}
          </div>
        ))}
      </>
    )
  }

  return (
    <StyledTeamMemberRow teamMemberRowClickEnabled={teamMemberRowClickEnabled}>
      <StyledTeamMemberWrapper>
        <NameAndContact>
          <div className="name-pill-info width-100">
            {hasName ? (
              <div className="display-flex width-100">
                <strong>{getName()}</strong>
                <Icon />
              </div>
            ) : (
              <>{nameAndEmail()}</>
            )}
          </div>
          {hasName && <>{nameAndEmail()}</>}
        </NameAndContact>
        <Roles>{displayRoles()}</Roles>
        <LoginAndInvites>
          {item.statusAccepted ? (
            <>
              <b>Last login:</b> {item.lastLogin}
              <br />
              <b>IP Address:</b> {item.lastLoginIP}
            </>
          ) : (
            <>
              <b>Invited:</b> {item.invitedAt}
              <br />
              <b>Expires:</b> {item.inviteExpiresAt}
            </>
          )}
        </LoginAndInvites>
        <Box>
          {showToolsButton && statusAccepted && (
            <Display.ToolsButton tools={shownMemberTools as any} />
          )}
        </Box>
      </StyledTeamMemberWrapper>
    </StyledTeamMemberRow>
  )
}
